import { onboardingAPI } from '../../services/api';

export type StepStatus = 'done' | 'action_needed' | 'skipped' | 'not_applicable';

export interface OnboardingStep {
  key: string;
  title: string;
  description: string;
  required: boolean;
  status: StepStatus;
  evidence: string;
  details: Record<string, any>;
  warning: boolean;
  fix_route: string;
  skip_reason?: string | null;
  user_action?: { status: string; updated_at: string; updated_by: number | null } | null;
}

export interface OnboardingProgress {
  percent: number;
  required_percent: number;
  required_total: number;
  required_complete: number;
  optional_total: number;
  optional_complete: number;
  applicable_total: number;
  complete: number;
}

export interface OnboardingState {
  version: number;
  current_version: number;
  started_at: string | null;
  completed_at: string | null;
  dismissed_until: string | null;
  snoozed?: boolean;
  last_reminded_at: string | null;
}

export type OnboardingReason = 'new' | 'upgrade' | 'incomplete' | null;

export interface AssetChecklist {
  asset: { id: number; name: string; organisation_id: number; platforms: Record<string, boolean> };
  steps: OnboardingStep[];
  progress: OnboardingProgress;
  required_complete: boolean;
  state: OnboardingState;
  needs_onboarding: boolean;
  reason: OnboardingReason;
  last_activity_at: string | null;
  can_edit?: boolean;
  integration?: { tracking_id: string; api_key: string; integration_script_url: string; integration_code: string } | null;
}

export interface AssetSummary {
  asset: AssetChecklist['asset'];
  progress: OnboardingProgress;
  required_complete: boolean;
  needs_onboarding: boolean;
  reason: OnboardingReason;
  state: OnboardingState;
  last_activity_at: string | null;
  next_step: OnboardingStep | null;
  warnings: { key: string; evidence: string }[];
}

export const STATUS_META: Record<StepStatus, { label: string; icon: string; color: string; bg: string }> = {
  done: { label: 'Done', icon: 'bi-check-circle-fill', color: '#059669', bg: '#ecfdf5' },
  action_needed: { label: 'Action needed', icon: 'bi-circle', color: '#d97706', bg: '#fffbeb' },
  skipped: { label: 'Skipped', icon: 'bi-skip-forward-circle', color: '#64748b', bg: '#f1f5f9' },
  not_applicable: { label: 'Not applicable', icon: 'bi-dash-circle', color: '#94a3b8', bg: '#f8fafc' },
};

export const REASON_TEXT: Record<string, string> = {
  upgrade: "We've upgraded CloudAyn — let's re-check your setup",
  new: 'Finish setting up this asset',
  incomplete: 'Some setup steps need attention',
};

export const progressColor = (pct: number) => (pct >= 100 ? '#059669' : pct >= 50 ? '#4f46e5' : '#d97706');

// One overview request per organisation shared by every badge on a page.
const overviewCache = new Map<string, { at: number; promise: Promise<AssetSummary[]> }>();
export const fetchOverviewCached = (organisationId?: number): Promise<AssetSummary[]> => {
  const key = String(organisationId || 'default');
  const hit = overviewCache.get(key);
  if (hit && Date.now() - hit.at < 30_000) return hit.promise;
  const promise = onboardingAPI
    .overview(organisationId)
    .then((r) => (r.data?.data?.assets || []) as AssetSummary[])
    .catch(() => [] as AssetSummary[]);
  overviewCache.set(key, { at: Date.now(), promise });
  return promise;
};
export const invalidateOverviewCache = () => overviewCache.clear();
